import React, { useCallback, useEffect, useState } from "react"
import {
  Row, Col, CardHeader, CardBody, Spinner, Button, Input, Label, FormGroup,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import { toast, ToastContainer } from "react-toastify"
import { getModernTableStyles } from "../Tables/modernTableStyles"
import {
  adminGetReferralSettings, adminUpdateReferralSettings, adminGetReferrals, adminCancelReferral,
} from "../../api/requests/referral-requests"

const NAVY = "#221C5D"
const PAGE_SIZE = 25

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const statusMap = {
  PENDING: { label: "Paket seçmesi bekleniyor", color: "#F2C96D" },
  QUALIFIED: { label: "Ödül ekleniyor", color: "#E56A22" },
  REWARDED: { label: "Tamamlandı", color: "#71C30B" },
  CANCELLED: { label: "İptal", color: "#B0B7C3" },
  REJECTED: { label: "Reddedildi", color: "#E07A7A" },
}

const rewardStatusMap = {
  PENDING: "Bekliyor",
  ON_HOLD: "Aktif abonelik bekleniyor",
  APPLIED: "Eklendi",
  CANCELLED: "İptal",
  REVOKED: "Geri alındı",
}

const reasonMap = {
  SAME_PERSON: "Davet edenle aynı kişi",
  SAME_TAX_IDENTITY: "Davet edenle aynı VKN/TCKN",
  EXISTING_CUSTOMER: "Mevcut müşteri",
  PROGRAM_CLOSED: "Program kapalıydı",
  INVALID_CODE: "Geçersiz kod",
  PAYMENT_REFUNDED: "Ödeme iade edildi",
  COMPANY_DELETED: "Firma silindi",
}

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "-")

const emptyToNull = (v) => (v === "" || v === undefined ? null : v)

const ReferralProgramAdmin = () => {
  const [settings, setSettings] = useState(null)
  const [saving, setSaving] = useState(false)
  const [list, setList] = useState({ content: [], totalElements: 0, totalPages: 0 })
  const [listLoading, setListLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [statusFilter, setStatusFilter] = useState("")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [cancelTarget, setCancelTarget] = useState(null)
  const [cancelReason, setCancelReason] = useState("")

  document.title = "Referans Programı | Tahsilat360"

  const styles = `${getModernTableStyles("referral-admin-modern")}`

  useEffect(() => {
    adminGetReferralSettings()
      .then((res) => setSettings(res.data))
      .catch((err) => toast.error(err.message, { position: "top-right", autoClose: 3000 }))
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400)
    return () => clearTimeout(t)
  }, [search])

  const loadList = useCallback(async () => {
    setListLoading(true)
    try {
      const res = await adminGetReferrals({
        status: statusFilter, search: debouncedSearch, page, size: PAGE_SIZE,
      })
      setList(res.data || { content: [], totalElements: 0, totalPages: 0 })
    } catch (err) {
      toast.error(err.message, { position: "top-right", autoClose: 3000 })
    } finally {
      setListLoading(false)
    }
  }, [statusFilter, debouncedSearch, page])

  useEffect(() => { loadList() }, [loadList])

  const setField = (field, value) => setSettings((prev) => ({ ...prev, [field]: value }))

  const saveSettings = async () => {
    setSaving(true)
    try {
      const payload = {
        ...settings,
        referrerRewardMonths: Number(settings.referrerRewardMonths),
        referredRewardMonths: Number(settings.referredRewardMonths),
        startsOn: emptyToNull(settings.startsOn),
        endsOn: emptyToNull(settings.endsOn),
        termsText: emptyToNull(settings.termsText),
      }
      const res = await adminUpdateReferralSettings(payload)
      setSettings(res.data)
      toast.success("Ayarlar kaydedildi", { position: "top-right", autoClose: 2000 })
    } catch (err) {
      toast.error(err.message, { position: "top-right", autoClose: 4000 })
    } finally {
      setSaving(false)
    }
  }

  const confirmCancel = async () => {
    try {
      await adminCancelReferral(cancelTarget.id, cancelReason.trim())
      toast.success("Referans iptal edildi", { position: "top-right", autoClose: 2000 })
      setCancelTarget(null)
      setCancelReason("")
      loadList()
    } catch (err) {
      toast.error(err.message, { position: "top-right", autoClose: 3000 })
    }
  }

  const renderStatus = (item) => {
    const s = statusMap[item.status] || { label: item.status, color: "#B0B7C3" }
    return (
      <>
        <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: s.color, color: "#fff" }}>
          {s.label}
        </span>
        {item.statusReason && (
          <span className="text-muted d-block" style={{ fontSize: ".8rem" }}>
            {reasonMap[item.statusReason] || item.statusReason}
          </span>
        )}
      </>
    )
  }

  const renderReward = (status, months, newEndDate) => {
    if (!status) return months > 0 ? `${months} ay` : "-"
    return (
      <>
        {months} ay · {rewardStatusMap[status] || status}
        {newEndDate && (
          <span className="text-muted d-block" style={{ fontSize: ".8rem" }}>Yeni bitiş: {formatDate(newEndDate)}</span>
        )}
      </>
    )
  }

  return (
    <div className="page-content referral-admin-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <Row className="mb-3">
          <Col>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4">
                <h5 className="mb-1 fw-bold text-dark">Referans Programı Ayarları</h5>
                <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                  Değişiklikler yalnız bundan sonra oluşan davetlere uygulanır; mevcut davetler oluştukları andaki
                  kuralla tamamlanır.
                </p>
              </CardHeader>
              <CardBody>
                {!settings ? (
                  <div className="text-center p-4"><Spinner color="primary" /></div>
                ) : (
                  <>
                    <FormGroup switch className="mb-3">
                      <Input
                        type="switch"
                        checked={!!settings.enabled}
                        onChange={(e) => setField("enabled", e.target.checked)}
                      />
                      <Label check className="fw-semibold">Program açık</Label>
                    </FormGroup>
                    <Row>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Davet edene (ay)</Label>
                          <Input type="number" min={0} max={24} value={settings.referrerRewardMonths ?? ""}
                                 onChange={(e) => setField("referrerRewardMonths", e.target.value)} />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Davet edilene (ay)</Label>
                          <Input type="number" min={0} max={24} value={settings.referredRewardMonths ?? ""}
                                 onChange={(e) => setField("referredRewardMonths", e.target.value)} />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Başlangıç tarihi</Label>
                          <Input type="date" value={settings.startsOn || ""}
                                 onChange={(e) => setField("startsOn", e.target.value)} />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Bitiş tarihi</Label>
                          <Input type="date" value={settings.endsOn || ""}
                                 onChange={(e) => setField("endsOn", e.target.value)} />
                        </FormGroup>
                      </Col>
                    </Row>
                    <FormGroup>
                      <Label>Kampanya koşulları (firmalara gösterilir)</Label>
                      <Input type="textarea" rows={3} maxLength={4000} value={settings.termsText || ""}
                             onChange={(e) => setField("termsText", e.target.value)} />
                    </FormGroup>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        {settings.updatedAt ? `Son değişiklik: ${new Date(settings.updatedAt).toLocaleString("tr-TR")}` : ""}
                      </small>
                      <Button onClick={saveSettings} disabled={saving}
                              style={{ backgroundColor: NAVY, borderColor: NAVY }}>
                        {saving ? <Spinner size="sm" /> : "Kaydet"}
                      </Button>
                    </div>
                  </>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>

        <Row>
          <Col>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4">
                <div className="d-flex flex-wrap justify-content-between align-items-end gap-2">
                  <div>
                    <h5 className="mb-1 fw-bold text-dark">Referanslar</h5>
                    <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                      {list.totalElements > 0 ? `Toplam ${list.totalElements} kayıt` : "Kayıt bulunamadı"}
                    </p>
                  </div>
                  <div className="d-flex gap-2">
                    <Input type="select" value={statusFilter} style={{ width: 200 }}
                           onChange={(e) => { setStatusFilter(e.target.value); setPage(0) }}>
                      <option value="">Tüm durumlar</option>
                      {Object.entries(statusMap).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </Input>
                    <Input placeholder="Firma adı veya kod ara" value={search} style={{ width: 240 }}
                           onChange={(e) => { setSearch(e.target.value); setPage(0) }} />
                  </div>
                </div>
              </CardHeader>
              <CardBody className="pt-2">
                {listLoading ? (
                  <div className="text-center p-5"><Spinner color="primary" /></div>
                ) : list.content.length === 0 ? (
                  <div className="text-center py-5 text-muted">Kayıt yok.</div>
                ) : (
                  <>
                    <div className="table-responsive rounded-4 border">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th>Davet eden</Th>
                            <Th>Davet edilen</Th>
                            <Th>Kod</Th>
                            <Th>Tarih</Th>
                            <Th>Durum</Th>
                            <Th>Davet eden ödülü</Th>
                            <Th>Davet edilen ödülü</Th>
                            <Th className="text-center">İşlem</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {list.content.map((item) => (
                            <Tr key={item.id} className="border-bottom">
                              <Td className="fw-semibold text-dark">{item.referrerCompanyName}</Td>
                              <Td className="fw-semibold text-dark">{item.referredCompanyName}</Td>
                              <Td>{item.referralCode}</Td>
                              <Td>{formatDate(item.createdAt)}</Td>
                              <Td>{renderStatus(item)}</Td>
                              <Td>{renderReward(item.referrerRewardStatus, item.referrerRewardMonths, item.referrerNewEndDate)}</Td>
                              <Td>{renderReward(item.referredRewardStatus, item.referredRewardMonths, item.referredNewEndDate)}</Td>
                              <Td className="text-center">
                                {(item.status === "PENDING" || item.status === "QUALIFIED") && (
                                  <Button size="sm" color="link" className="text-danger p-0"
                                          onClick={() => setCancelTarget(item)}>
                                    İptal et
                                  </Button>
                                )}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </div>
                    {list.totalPages > 1 && (
                      <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                        <Button size="sm" className="btn-rounded btn-navy-outline" disabled={page === 0}
                                onClick={() => setPage(page - 1)}>Önceki</Button>
                        <span className="text-muted" style={{ fontSize: ".9rem" }}>{page + 1} / {list.totalPages}</span>
                        <Button size="sm" className="btn-rounded btn-navy-outline" disabled={page + 1 >= list.totalPages}
                                onClick={() => setPage(page + 1)}>Sonraki</Button>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>

      <Modal isOpen={!!cancelTarget} toggle={() => setCancelTarget(null)} centered>
        <ModalHeader toggle={() => setCancelTarget(null)}>Referansı iptal et</ModalHeader>
        <ModalBody>
          <p className="mb-2">
            <strong>{cancelTarget?.referrerCompanyName}</strong> → <strong>{cancelTarget?.referredCompanyName}</strong>
          </p>
          <p className="text-muted" style={{ fontSize: ".9rem" }}>
            Henüz eklenmemiş ödüller iptal olur. Aboneliğe eklenmiş süre bu işlemle geri alınmaz;
            davet edilenin ödemesini iade ederseniz süreler iki taraftan da otomatik geri alınır.
          </p>
          <Label>Gerekçe</Label>
          <Input maxLength={64} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setCancelTarget(null)}>Vazgeç</Button>
          <Button color="danger" disabled={!cancelReason.trim()} onClick={confirmCancel}>İptal et</Button>
        </ModalFooter>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  )
}

export default ReferralProgramAdmin
